/**
 * The Body tab's "World check" card stack (ADR-040): the guided repair drawn
 * from `intakeView`. One card per finding, each with its status mark; the
 * questions jump to the section where the object's tool already lives (no tour
 * engine — the Body accordion is the tour); every pass ends on the walk preview
 * and the export.
 *
 * TEXT ONLY. World's name and reasons are display text from another page:
 * everything here is set with textContent, never innerHTML.
 *
 * Depends on: intake-session (state, export), intake-view, status-mark.
 * Depended on by: main.ts (mounted as the Body tab's `intake` tool).
 */
import type { PanelMount } from './panel-registry.js';
import { statusMark } from './status-mark.js';
import type { IntakeSession } from '../intake/intake-session.js';
import { intakeView, type IntakeJump } from '../intake/intake-view.js';

export interface IntakePanelDeps {
  readonly session: IntakeSession;
  /** Back to World: the one way out the guided frame uses (focus the World tab that opened Harry, else ask, else go). */
  readonly returnToWorld: () => void;
  /** Open the Body section where a question's object lives. */
  readonly jump: (to: IntakeJump) => void;
  /** Play the subject's walk (playback mode, the clip it wears). */
  readonly playWalk: () => void;
}

const el = <K extends keyof HTMLElementTagNameMap>(tag: K, className: string, text?: string): HTMLElementTagNameMap[K] => {
  const node = document.createElement(tag);
  node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
};

export function createIntakePanel(deps: IntakePanelDeps): PanelMount {
  return (container) => {
    const root = el('div', 'intake-panel');
    root.dataset['testid'] = 'intake';
    container.append(root);
    const render = (): void => {
      const s = deps.session.state();
      if (s === null) {
        root.replaceChildren();
        return;
      }
      const v = intakeView(s);
      const children: HTMLElement[] = [el('p', 'intake-headline', v.headline)];
      if (v.worldSaid.length > 0) {
        const said = el('div', 'hand-card intake-world');
        said.append(el('p', 'intake-world-label', 'World said'));
        const list = el('ul', 'intake-world-reasons');
        for (const reason of v.worldSaid) list.append(el('li', '', reason));
        said.append(list);
        children.push(said);
      }
      for (const card of v.cards) {
        const node = el('div', 'hand-card intake-card');
        node.dataset['code'] = card.code;
        node.dataset['state'] = card.state;
        const head = el('div', 'intake-card-head');
        head.append(statusMark(card.state, { subject: card.title, pill: card.pill }), el('strong', 'intake-card-title', card.title));
        node.append(head, el('p', 'intake-card-line', card.line));
        const act = (label: string, primary: boolean, onClick: () => void, disabled = false): void => {
          const b = el('button', primary ? 'hand-graft-now' : 'hand-revert', label);
          b.type = 'button';
          b.dataset['action'] = card.code;
          b.disabled = disabled;
          b.addEventListener('click', onClick);
          node.append(b);
        };
        if (card.jump !== undefined) {
          const to = card.jump.to;
          act(card.jump.label, false, () => deps.jump(to));
        }
        if (card.code === 'walk') act('Play the walk', false, () => deps.playWalk());
        if (card.code === 'export') {
          act(s.exported === null ? 'Export for World' : 'Export again', s.exported === null, () => void deps.session.exportForWorld(), !v.canExport);
          if (s.exported?.savedForWorld === true) {
            // The SAME way out as the guided Done (Allen 2026-10-04): back to the World tab that opened Harry —
            // never a fresh World page loaded in this tab (the old `<a href>` did that).
            const back = el('button', 'intake-back', 'Back to World ›');
            back.type = 'button';
            back.dataset['action'] = 'back-to-world';
            back.addEventListener('click', () => deps.returnToWorld());
            node.append(back);
          }
        }
        children.push(node);
      }
      const close = el('button', 'hand-revert intake-close', 'Close World check');
      close.type = 'button';
      close.addEventListener('click', () => deps.session.close());
      children.push(close);
      root.replaceChildren(...children);
    };
    const unsubscribe = deps.session.subscribe(render);
    render();
    return {
      dispose() {
        unsubscribe();
        root.remove();
      },
    };
  };
}
